import { useState } from 'react'
import * as Tooltip from '@radix-ui/react-tooltip'
import { X } from 'lucide-react'

const EsporaiTooltip = ({
  children,
  content,
  inverse = false,
  delay = 200,
  position = 'top',
  noTooltip = false,
  contentClassName = '',
  align = 'center',
  showOverlay = false,
  showCloseButton = false,
  scrollable = false,
  scrollClassName = '',
  openOn = 'hover', // 'hover' | 'click'
}) => {
  const [open, setOpen] = useState(false)

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640
  const actualPosition = isMobile ? 'bottom' : position

  // Importante: el modal de optimizaciones usa z-[10100]. El tooltip debe quedar por encima.
  const contentClasses = `
    z-[10250] px-3 py-1.5 text-xs font-medium rounded-card shadow-card-hover border whitespace-normal
    max-w-xs max-w-[calc(100vw-32px)]
    ${inverse
      ? 'bg-white text-primary border-white'
      : 'bg-primary text-white border-primary'
    }
    ${contentClassName || ''}`

  const isStringContent = typeof content === 'string'

  const handleToggle = () => setOpen(prev => !prev)
  const handleClose = () => setOpen(false)
  const isClickOnly = openOn === 'click'
  const handleOpenChange = (nextOpen) => {
    if (!isClickOnly) {
      setOpen(nextOpen)
      return
    }
    // En modo click-only, ignoramos aperturas por hover/focus;
    // pero permitimos cierres (Escape / click-outside / etc.).
    if (!nextOpen) setOpen(false)
  }

  // If noTooltip is true, just render children without tooltip
  if (noTooltip) {
    return <>{children}</>;
  }

  return (
    <Tooltip.Provider delayDuration={delay}>
      <Tooltip.Root open={open} onOpenChange={handleOpenChange}>
        <Tooltip.Trigger asChild>
          <div
            role="button"
            tabIndex={0}
            onPointerDown={e => {
              e.preventDefault();
              e.stopPropagation();
              handleToggle();
            }}
            onPointerMove={e => {
              if (!isClickOnly) return
              e.preventDefault()
              e.stopPropagation()
            }}
            onPointerEnter={e => {
              if (!isClickOnly) return
              e.preventDefault()
              e.stopPropagation()
            }}
            onFocus={e => {
              if (!isClickOnly) return
              e.preventDefault()
              e.stopPropagation()
            }}
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onBlur={handleClose}
          >
            {children}
          </div>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <div>
            {showOverlay && open ? (
              <div
                className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
                onPointerDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleClose()
                }}
              />
            ) : null}
            <Tooltip.Content
              side={actualPosition}
              align={align}
              sideOffset={8}
              className={`${contentClasses} relative`}
            >
              {showCloseButton ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleClose()
                  }}
                  className="absolute top-2 right-2 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/15 border border-white/10 hover:border-white/20 text-white/80 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                  aria-label="Cerrar"
                >
                  <X className="w-4 h-4" />
                </button>
              ) : null}
              <div className={scrollable ? (scrollClassName || 'max-h-[70vh] overflow-y-auto') : ''}>
                {isStringContent
                  ? content.split("\\n").map((line, index) => (
                    <div key={index}>{line}</div>
                  ))
                  : content}
              </div>
              <Tooltip.Arrow
                className={`fill-current ${inverse ? 'text-white' : 'text-primary'}`}
              />
            </Tooltip.Content>
          </div>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  )
}

export default EsporaiTooltip
